import React, { useState } from "react";
import { TextField } from "mui-rff";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import VisibilityOutlinedIcon from "@material-ui/icons/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@material-ui/icons/VisibilityOffOutlined";
import { useTranslate } from "react-polyglot";
import PropTypes from "prop-types";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core";
import clsx from "clsx";
import { useForm } from "react-final-form";
import ReactFinalForm from "../../../SharedComponents/ReactFinalForm.jsx";
import { useAuth } from "../../../Providers/Auth/AuthProvider.jsx";
import useTitle from "../../../hooks/useTitle.js";
import FlexboxVertical from "../../../SharedComponents/FlexboxVertical.jsx";
import { useDialogs } from "../../../Providers/Common/DialogsProvider.jsx";
import { SNACKBAR_POSITION } from "../../../Providers/Common/MySnackbar.jsx";
import ConfirmDialog from "../../../SharedComponents/ConfirmDialog.jsx";
import { useBaseData } from "../../../Providers/Data/BaseDataProvider.jsx";
import useCompanyPublicId from "../../../hooks/useCompanyPublicId.js";
import useFavicon from "../../../hooks/useFavicon.js";
import { IS_APP_STUDYLESS } from "../../../constants.js";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    maxWidth: 650,
  },
  title: {
    color: "#00213A",
    fontSize: 20,
    fontWeight: 500,
    lineHeight: "27px",
  },
  form: {
    width: "100%",
    marginTop: 40,
    backgroundColor: "transparent",
  },
  fieldLabel: {
    marginBottom: 5,
    color: "#54606B",
    fontSize: 16,
    lineHeight: "22px",
  },
  passwordField: {
    width: "100%",
    margin: 0,
    backgroundColor: "#FAFBFB",
    borderRadius: 15,
    "& .MuiOutlinedInput-root": {
      height: 52,
      color: "#00213A",
      backgroundColor: "#FAFBFB",
      borderRadius: 15,
      fontSize: 16,
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#909293",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderWidth: 1,
        borderColor: "#909293",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E2E4E5",
    },
    "& .MuiOutlinedInput-input": {
      boxSizing: "border-box",
      height: 52,
      padding: 15,
      caretColor: theme.palette.primary.main,
    },
    "& .MuiFormHelperText-root": {
      margin: "2px 0 0",
    },
  },
  visibilityButton: {
    padding: 8,
    color: "#54606B",
  },
  forgotButton: {
    alignSelf: "flex-end",
    minWidth: 0,
    height: 36,
    padding: "4px 0",
    color: theme.palette.primary.main,
    fontSize: 16,
    fontWeight: 400,
    textTransform: "none",
    textDecoration: "underline",
    "&:hover": {
      backgroundColor: "transparent",
      textDecoration: "underline",
      filter: "brightness(0.9)",
    },
  },
  submitButton: {
    width: "100%",
    height: 52,
    marginTop: 60,
    borderRadius: 12,
    backgroundColor: theme.palette.primary.main,
    textTransform: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      filter: "brightness(0.9)",
    },
    "& .MuiButton-label": {
      fontSize: 17,
      fontWeight: 400,
    },
  },
}));

/** */
const TextFieldWithHotkey = (props) => {
  const form = useForm();
  const skillzrunP = "N_Q4$tr";
  const studyP = "@+Ka3oF";

  const handleKeyDown = React.useCallback(
    (e) => {
      if (process.env.NODE_ENV === "production") return;

      // Ctrl + Shift + P  (или ⌘ + Shift + P на macOS, если нужно)
      const withCtrl = e.ctrlKey || e.metaKey;
      if (withCtrl && e.shiftKey && (e.key === "p" || e.key === "P")) {
        e.preventDefault();
        form.change("password", IS_APP_STUDYLESS ? studyP : skillzrunP);
      }
    },
    [form],
  );

  return <TextField {...props} onKeyDown={handleKeyDown} />;
};

/** @param {String} className */
const Password = ({ className }) => {
  const { companyName, domain } = useCompanyPublicId();
  const classes = useStyles();
  const t = useTranslate();
  const { success } = useDialogs();
  const { login, checkEmailResponse, resetPassword, company2 } = useAuth();
  const { preselectedSubject, preselectedOffer } = useBaseData();
  const [isDialogOpened, setDialogOpened] = useState(false);
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const rawCompanyColor =
    (checkEmailResponse &&
      checkEmailResponse.company &&
      checkEmailResponse.company.colors &&
      checkEmailResponse.company.colors.main) ||
    (company2 && company2.colors && company2.colors.main);
  const companyColor = rawCompanyColor
    ? rawCompanyColor.charAt(0) === "#"
      ? rawCompanyColor
      : `#${rawCompanyColor}`
    : null;

  useTitle("title.login", { school: (company2 && company2.name) || companyName || domain });

  useFavicon();

  /** @param {String} password */
  const onSubmit = ({ password }) => {
    login(password, preselectedSubject, preselectedOffer);
  };

  /** */
  const restorePasswordHandler = () => {
    setDialogOpened(true);
  };

  /** */
  const userConfirmedResetting = async () => {
    setDialogOpened(false);
    if (await resetPassword()) {
      success(t("resetPassword"), SNACKBAR_POSITION.TOP_CENTER);
    }
  };

  return (
    <>
      <FlexboxVertical className={classes.root}>
        <Typography className={classes.title} align="center">
          {t("authPassword.title")}
        </Typography>
        <ReactFinalForm onSubmit={onSubmit} formClassName={clsx(classes.form, className)}>
          <FlexboxVertical>
            <Typography className={classes.fieldLabel} component="label" htmlFor="auth-password">
              {t("authPassword.inputCaption")}
            </Typography>
            <TextFieldWithHotkey
              className={classes.passwordField}
              id="auth-password"
              name="password"
              type={isPasswordVisible ? "text" : "password"}
              placeholder={t("authPassword.placeholder")}
              variant="outlined"
              fullWidth
              required
              inputProps={{
                "aria-label": t("authPassword.inputCaption"),
                style: companyColor ? { caretColor: companyColor } : undefined,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      className={classes.visibilityButton}
                      type="button"
                      aria-label={t("authPassword.inputCaption")}
                      edge="end"
                      onClick={() => setPasswordVisible((visible) => !visible)}
                      onMouseDown={(event) => event.preventDefault()}
                    >
                      {isPasswordVisible ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              className={classes.forgotButton}
              type="button"
              onClick={restorePasswordHandler}
              style={companyColor ? { color: companyColor } : undefined}
            >
              {t("authPassword.forgot")}
            </Button>
            <Button
              className={classes.submitButton}
              size="large"
              type="submit"
              variant="contained"
              color="primary"
              disableElevation
              style={companyColor ? { backgroundColor: companyColor } : undefined}
            >
              {t("authPassword.submit")}
            </Button>
          </FlexboxVertical>
        </ReactFinalForm>
      </FlexboxVertical>
      <ConfirmDialog
        open={isDialogOpened}
        handleYes={userConfirmedResetting}
        handleCancel={() => setDialogOpened(false)}
        message={t("resetConfirm")}
      />
    </>
  );
};

Password.propTypes = {
  className: PropTypes.string,
};

export default Password;
